import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Button, Divider, Form, Input, Layout, Modal, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useLocation, useParams } from 'react-router-dom'
import store from 'store2'
import styled from 'styled-components'
import queryString from 'query-string'

import { JWT_TOKEN_NAME, REFRESH_TOKEN_NAME } from '../../constants/auth-key'
import { ImageLinks } from '../../constants/image-link'
import PageUrls from '../../constants/page-urls'
import { axiosInstance } from '../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../constants/errors'
import { sendToErrorLogUnAuth } from '../../utils/logs'
import {
  clearMerchantScopedCache,
  notifyMerchantSwitched,
} from '../../utils/cache'
import { IMerchantOption } from '../../types/merchant'
import MerchantOptionSelect from '../../components/MerchantSwitch/MerchantOptionSelect'
import MerchantStatusTags from '../../components/MerchantSwitch/MerchantStatusTags'
import { IUser } from '../../types/user'

const errorModal = (statusCode: number, message?: string) => {
  const errorDetails = [
    {
      statusCode: 400,
      title: 'การเข้าสู่ระบบล้มเหลว',
      content:
        message ||
        'ไม่สามารถเข้าสู่ระบบได้ กรุณาตรวจสอบชื่อผู้ใช้ หรือรหัสผ่านอีกครั้ง',
    },
    {
      statusCode: 401,
      title: 'การเข้าสู่ระบบล้มเหลว',
      content:
        'ไม่สามารถเข้าสู่ระบบได้ กรุณาตรวจสอบชื่อผู้ใช้ หรือรหัสผ่านอีกครั้ง',
    },
    {
      statusCode: 429,
      content:
        'ไม่สามารถเข้าสู่ระบบได้ เนื่องจากมีการร้องขอใช้ระบบมากเกินไป กรุณารอซักครู่',
    },
    {
      statusCode: 403,
      content: 'ไม่สามารถเข้าสู่ระบบได้ เนื่องจากผู้ใช้นี้ถูกระงับการเข้าถึง',
    },
  ]

  if (message) {
    Modal.error({
      title: 'การเข้าสู่ระบบล้มเหลว!',
      content: message,
    })
    return
  }

  const error = errorDetails.find((error) => error.statusCode === statusCode)
  Modal.error({
    title: 'การเข้าสู่ระบบล้มเหลว!',
    content: error ? error.content : 'กรุณาลองใหม่อีกครั้ง!',
  })
}

interface Props {
  message?: string
}

const LoginPage = (props: Props) => {
  const [form] = Form.useForm() as any
  const location = useLocation()
  const [showInputMerchant, setShowInputMerchant] = useState<boolean>(false)
  const [submitingMerchant, setSubmitingMerchant] = useState<boolean>(false)
  const [merchant, setMerchant] = useState<string>('')
  const [token, setToken] = useState({
    refreshToken: '',
    accessToken: '',
    username: '',
  })
  const [currentMerchant, setCurrentMerchant] =
    useState<IMerchantOption | null>(null)

  const adminHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token.accessToken}` }),
    [token.accessToken],
  )

  const queryStr = queryString.parse(window.location.search)
  const message = queryStr.message as string

  const { executeRecaptcha } = useGoogleReCaptcha()

  useEffect(() => {
    if (
      location.pathname === PageUrls.Login ||
      location.pathname === PageUrls.Logout
    )
      return
    else {
      window.location.href = `${PageUrls.Login}?forward=${location.pathname}`
    }
  }, [])

  useEffect(() => {
    if (message || props.message) {
      const str = message || props?.message || 'unauth default'
      sendError(str)
    }
  }, [message, props.message])

  const sendError = async (message: string) => {
    try {
      const token = store.get(JWT_TOKEN_NAME) || ''
      const errorDetails = {
        message,
        timestamp: new Date().toISOString(),
        token,
      }
      await sendToErrorLogUnAuth(errorDetails)
    } catch (e: any) {
      console.log(`error: ${e?.message}`)
    }
  }

  const query = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    return searchParams.get('forward')
  }, [location.search])

  useEffect(() => {
    if (!showInputMerchant) {
      return
    }
    let cancelled = false
    const loadCurrentMerchant = async () => {
      try {
        const { data: me } = await axiosInstance.get<Partial<IUser>>(
          '/users/me',
          { headers: adminHeaders },
        )
        const prefix = me?.merchantPrefix
        if (!prefix || cancelled) {
          return
        }
        const { data: options } = await axiosInstance.get<IMerchantOption[]>(
          '/merchant/options',
          { headers: adminHeaders, params: { search: prefix } },
        )
        if (!cancelled) {
          setCurrentMerchant(
            options?.find((option) => option.prefix === prefix) ?? {
              prefix,
              name: prefix,
            },
          )
        }
      } catch (error) {
        // ไม่รู้ร้านเดิม → ให้เลือกร้านใหม่อย่างเดียว
      }
    }
    loadCurrentMerchant()
    return () => {
      cancelled = true
    }
  }, [showInputMerchant, adminHeaders])

  const onFinish = async (values: any) => {
    if (!executeRecaptcha) {
      return
    }
    const token = await executeRecaptcha('login')

    try {
      const response = await axiosInstance.post<{
        refreshToken: string
        accessToken: string
        permission: string
      }>(
        `/auth/login`,
        {
          username: values?.username?.trim() as string,
          password: values?.password?.trim() as string,
          //เคยปิด
          token,
        },
        { method: 'POST' },
      )
      clearMerchantScopedCache()
      if (response.data.permission === 'admin') {
        // interceptor ของ axios แนบ token ที่ค้างในเครื่องทับ header ที่ส่งเอง
        // → ต้องล้างของเก่าก่อน ไม่งั้นเลือกร้านในนามบัญชีเดิม
        store.remove(JWT_TOKEN_NAME)
        store.remove(REFRESH_TOKEN_NAME)
        setShowInputMerchant(true)
        setToken({
          refreshToken: response.data.refreshToken,
          accessToken: response.data.accessToken,
          username: values.username,
        })
        return
      }
      store.set(JWT_TOKEN_NAME, response.data.accessToken)
      store.set(REFRESH_TOKEN_NAME, response.data?.refreshToken)
      notifyMerchantSwitched()
      window.location.href = query ? query : PageUrls.Home
    } catch (error: any) {
      const statusCode = error?.response?.status ?? 500
      const message =
        ERRORS_MESSAGE_MAPPER?.[error.response?.data?.message ?? '']

      errorModal(statusCode, message)
    }
  }

  const enterWithAdminToken = () => {
    store.set(JWT_TOKEN_NAME, token.accessToken)
    store.set(REFRESH_TOKEN_NAME, token?.refreshToken)
    notifyMerchantSwitched()
    window.location.href = query ? query : PageUrls.Home
  }

  const handleUseCurrentMerchant = () => {
    setSubmitingMerchant(true)
    enterWithAdminToken()
  }

  const handleChangMerchant = async () => {
    setSubmitingMerchant(true)
    if (!merchant) {
      Modal.error({
        title: 'กรุณาเลือกร้าน!',
        content: 'กรุณาเลือกร้านก่อนทำการเข้าสู่ระบบ',
      })
      setSubmitingMerchant(false)
      return
    }
    try {
      await axiosInstance.put(
        '/users/username/merchant',
        {
          username: token.username,
          merchant: merchant,
        },
        {
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
          },
        },
      )
      enterWithAdminToken()
      return
    } catch (error: any) {
      // console.log(error)
      const statusCode = error?.response?.status ?? 500
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[error.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      errorModal(statusCode, messageError)
      setSubmitingMerchant(false)
    }
  }

  if (submitingMerchant) {
    return <></>
  }

  return (
    <Root>
      <LogoApp>
        <img src={ImageLinks.LOGO_HORIZONTAL} alt="logo" width="250" />
      </LogoApp>
      {showInputMerchant ? (
        <Form
          name="login-form"
          layout="vertical"
          onFinish={handleChangMerchant}
        >
          <WrapperForm>
            {currentMerchant && (
              <>
                <Form.Item label="ร้านปัจจุบัน">
                  <Typography.Text strong>
                    {currentMerchant.name}
                  </Typography.Text>{' '}
                  <Typography.Text type="secondary">
                    ({currentMerchant.prefix})
                  </Typography.Text>{' '}
                  <MerchantStatusTags
                    status={currentMerchant.status}
                    isExpired={currentMerchant.isExpired}
                  />
                </Form.Item>
                <Button type="primary" block onClick={handleUseCurrentMerchant}>
                  ใช้ร้านเดิม
                </Button>
                <Divider plain>หรือ</Divider>
              </>
            )}
            <Form.Item label="เปลี่ยนร้าน">
              <MerchantOptionSelect
                value={merchant}
                onChange={setMerchant}
                headers={adminHeaders}
              />
            </Form.Item>
            <Button
              type={currentMerchant ? 'default' : 'primary'}
              htmlType="submit"
              block
              disabled={!merchant}
            >
              เปลี่ยนร้าน
            </Button>
          </WrapperForm>
        </Form>
      ) : (
        <Form
          name="login-form"
          layout="vertical"
          form={form}
          onFinish={onFinish}
        >
          <WrapperForm>
            <>
              <Form.Item
                label="Username"
                name="username"
                rules={[
                  { required: true, message: 'กรุณากรอกชื่อบัญชีผู้ใช้!' },
                ]}
              >
                <Input
                  placeholder="Username"
                  prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                  onChange={(e) => {
                    const s = e.target.value.toLowerCase()
                    form.validateFields()
                    form.setFieldsValue({
                      username: s,
                    })
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: 'กรุณากรอกรหัสผ่าน!!' }]}
              >
                <Input.Password
                  placeholder="Password"
                  prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" block>
                Log in
              </Button>
            </>
          </WrapperForm>
        </Form>
      )}
      <QRcode>
        <img src={'/images/png/qr.png'} alt="qrcode" width="200" />
      </QRcode>
      <AddFriend>
        <a href="https://lin.ee/iMxFykU" target="_blank">
          <img src={'/images/png/line-btn.png'} alt="line-btn" width="200" />
        </a>
      </AddFriend>
    </Root>
  )
}

export default LoginPage

const Root = styled(Layout)`
  height: 100vh;
  padding-top: 6em;

  .ant-form {
    padding: 1.5em;
    border-radius: 6px;
    background: white;
    margin: 0 auto;
    width: 450px;
    box-shadow: 0 10px 34px -15px rgba(0, 0, 0, 0.24);
  }

  &.ant-layout {
    background: #fff url('/images/svg/gradient-bg.svg') no-repeat center center;
    background-size: cover;
    height: 100%;
    min-height: 100vh;
  }
`

const WrapperForm = styled.div`
  flex: 1 1;
`

const LogoApp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 0 1em 0;
  text-align: center;

  > div {
    width: 50px;
    height: 50px;
    background: gray;
  }
`

const QRcode = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1em;
  padding: 1em 0;
`

const AddFriend = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 0 1em 0;
`
